/**
 * One Dr Cleanstein conversation, laid out like the Claude app: a new chat opens
 * on a greeting with the ask box in the middle of the screen; once it starts,
 * the thread reads down one centred column and the ask box sits fixed below it.
 * Used full screen on the Ask page and narrower in the side panel.
 *
 * The chat carries on until the person starts a new one, so follow-ups such as
 * "and last year?" work. The page the person is on goes with each question, so
 * "this customer" means the one on screen.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import Modal from '../Modal';
import AskAnswer from './AskAnswer';
import AskAttachments from './AskAttachments';
import { askQuestion, loadConversation, logQuery, type DisplayMessage } from './askApi';
import { answerPlainText, downloadAnswerDoc, downloadChatDoc, downloadTable, fileDate } from './askExport';
import { formatNumber } from '../../utils/formatters';
import { ASSISTANT_AVATARS, ASSISTANT_NAMES, type AssistantRole } from '../../config/assistant';

const SUGGESTIONS = [
  'When was the last service for a customer, and what was it worth?',
  'My top 10 best value customers not serviced in the last 6 months',
  'What were our Peace of Sleep sales this year?',
  'Average service value per month this year',
];

interface AskChatProps {
  conversationId: string | null;
  onConversationChange: (id: string | null) => void;
  variant: 'page' | 'panel';
  /** The chat's stored title, for the header and the Word file. */
  title?: string | null;
  /** Called when an answer has finished, so a chat list can re-sort. */
  onAnswered?: () => void;
  /** Puts the cursor in the ask box on open. Defaults to on in the panel. */
  autoFocus?: boolean;
  /** The page's own actions for the open chat (star, move, rename, delete), in the header (Gary, 18 Oct). */
  headerActions?: ReactNode;
  /** Shown above a new chat's greeting, e.g. the project it will go in. */
  startNote?: ReactNode;
  /** Which assistant this chat talks to (Gary, 1 Nov). Ted unless given. */
  assistant?: AssistantRole;
  /** Starter questions for a new chat; Ted's are used when not given. */
  suggestions?: string[];
}

function greeting(firstName: string | null | undefined): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return firstName ? `${part}, ${firstName}` : part;
}

/** On a phone Return adds a line and the button sends; with a mouse and keyboard Return sends. */
function enterSends(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
}

export default function AskChat({ conversationId, onConversationChange, variant, title, onAnswered, autoFocus = variant === 'panel', headerActions, startNote, assistant = 'ted', suggestions = SUGGESTIONS }: AskChatProps) {
  const name = ASSISTANT_NAMES[assistant];
  const avatar = ASSISTANT_AVATARS[assistant];
  const { supabase, user } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [step, setStep] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lastCost, setLastCost] = useState<number | null>(null);
  const [queryFor, setQueryFor] = useState<DisplayMessage | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Follow the answer down only while the person is at the bottom, so reading
  // further up is never yanked away mid-answer.
  const atBottom = useRef(true);
  // The id the engine assigns to a brand-new chat, so the reload it triggers is skipped.
  const startedHere = useRef<string | null>(null);
  // Stops reading the answer if the panel closes or the page changes mid-answer.
  const abortRef = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  // Which send is current. Moving to another chat moves it on, so a reply still
  // arriving for the chat that was left can neither show here nor pull the
  // person back to it. The engine still saves that reply.
  const sendSeq = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; abortRef.current?.abort(); };
  }, []);

  // The chat on screen now, so a reply that finishes after the person has moved
  // to another chat does not pull the old one back in.
  const currentId = useRef(conversationId);
  currentId.current = conversationId;

  useEffect(() => {
    if (conversationId && startedHere.current === conversationId) return;
    sendSeq.current += 1;
    abortRef.current?.abort();
    setBusy(false);
    setLiveText('');
    setStep(null);
    setError(null);
    setLastCost(null);
    setMessages([]);
    atBottom.current = true;
    if (!conversationId) return;
    setLoading(true);
    loadConversation(supabase, conversationId)
      .then((m) => { if (currentId.current === conversationId) setMessages(m); })
      .catch(() => { if (currentId.current === conversationId) setError('Could not open that chat.'); })
      .finally(() => { if (currentId.current === conversationId) setLoading(false); });
  }, [conversationId, supabase]);

  useEffect(() => {
    const el = threadRef.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [messages, liveText, step, busy]);

  // The ask box grows with the question, up to its CSS max-height, then scrolls.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Seconds on the clock while an answer is being worked out.
  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [busy]);

  const send = useCallback(async (text: string) => {
    const question = text.trim();
    if (!question || busy) return;
    const mine = ++sendSeq.current;
    setBusy(true);
    setError(null);
    setDraft('');
    setLiveText('');
    setStep(null);
    setLastCost(null);
    atBottom.current = true;
    setMessages((m) => [...m, { id: `pending-${Date.now()}`, role: 'user', text: question }]);

    let activeId = conversationId;
    let answer = '';
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await askQuestion(
        supabase,
        { text: question, conversationId, pagePath: `${location.pathname}${location.search}`, role: assistant },
        (event) => {
          if (sendSeq.current !== mine) return;
          if (event.type === 'conversation') {
            activeId = event.id;
            if (event.id !== conversationId) {
              startedHere.current = event.id;
              onConversationChange(event.id);
            }
          } else if (event.type === 'step') {
            setStep(event.label);
          } else if (event.type === 'text') {
            answer += event.text;
            setLiveText(answer);
            setStep(null);
          } else if (event.type === 'error') {
            setError(event.message);
          } else if (event.type === 'done') {
            setLastCost(event.cost_usd);
          }
        },
        abort.signal,
      );
    } catch {
      if (!abort.signal.aborted) setError(`Lost the connection to ${name}. Please try again.`);
    } finally {
      abortRef.current = null;
    }
    if (!mounted.current || sendSeq.current !== mine) return;
    setBusy(false);
    setStep(null);
    setLiveText('');
    startedHere.current = null;
    // Reload from the store so every answer carries its saved id for Log a query.
    if (activeId && currentId.current === activeId) {
      try {
        setMessages(await loadConversation(supabase, activeId));
      } catch {
        if (answer) setMessages((m) => [...m, { id: '', role: 'assistant', text: answer }]);
      }
    }
    onAnswered?.();
  }, [busy, conversationId, location.pathname, location.search, onAnswered, onConversationChange, supabase, assistant, name]);

  const copyAnswer = async (text: string) => {
    try {
      await navigator.clipboard.writeText(answerPlainText(text));
      showToast('Copied. Tables paste straight into Excel.', 'success');
    } catch {
      showToast('Could not copy', 'error');
    }
  };

  const questionBefore = (idx: number): string | null => {
    for (let i = idx - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === 'user') return m.text;
    }
    return null;
  };

  const chatTitle = title || messages.find((m) => m.role === 'user')?.text.slice(0, 80) || `${name} chat`;

  const composer: ReactNode = (
    <form
      className="ask-composer"
      onSubmit={(e) => { e.preventDefault(); void send(draft); }}
    >
      <textarea
        ref={inputRef}
        className="ask-composer-input"
        rows={2}
        value={draft}
        placeholder={messages.length ? `Reply to ${name}` : `Ask ${name}`}
        aria-label={`Ask ${name}`}
        maxLength={4000}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && enterSends()) { e.preventDefault(); void send(draft); }
        }}
      />
      <div className="ask-composer-foot">
        <span className="ask-hint">{busy ? 'Answering…' : 'Customers, services, leads, or how to do something'}</span>
        <button type="submit" className="btn btn-primary ask-send" disabled={busy || !draft.trim()} aria-label="Ask" title="Ask (Enter)">
          ↑
        </button>
      </div>
    </form>
  );

  const isNew = messages.length === 0 && !busy && !loading;

  // The panel and the window open with the cursor in the ask box. The box is a
  // new element once a saved chat has loaded or a new chat starts, so focus again then.
  useEffect(() => {
    if (autoFocus && !loading) inputRef.current?.focus();
  }, [autoFocus, loading, isNew]);

  return (
    <div className={`ask-chat ask-chat--${variant}`}>
      {variant === 'page' && !isNew && (
        <div className="ask-chat-header">
          <span className="ask-chat-title">{chatTitle}</span>
          {headerActions}
          <button
            type="button"
            className="ask-ghost-btn"
            disabled={busy || messages.length === 0}
            onClick={() => downloadChatDoc(chatTitle, messages)}
          >
            Save chat as Word
          </button>
        </div>
      )}

      {isNew ? (
        <div className="ask-start">
          <div className="ask-start-inner">
            <img src={avatar} alt="" className="ask-avatar ask-avatar--lg" />
            <h2 className="ask-greeting">{greeting(user?.first_name)}</h2>
            {startNote}
            <p className="ask-start-sub">I look things up in the portal for you. Ask me anything about your customers, services and leads.</p>
            {error && <div className="ask-error" role="alert">{error}</div>}
            {composer}
            <div className="ask-suggestions">
              {suggestions.map((s) => (
                <button key={s} type="button" className="ask-chip" onClick={() => { setDraft(s); inputRef.current?.focus(); }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className="ask-thread"
            ref={threadRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
            }}
          >
            <div className="ask-column">
              {loading && <div className="ask-status">Opening the chat…</div>}

              {messages.map((m, idx) =>
                m.role === 'user' ? (
                  <div key={`${m.id}-${idx}`} className="ask-user">{m.text}</div>
                ) : (
                  <div key={`${m.id}-${idx}`} className="ask-reply">
                    <AskAnswer
                      text={m.text}
                      onExportTable={(t) => void downloadTable(t, `leo-${fileDate()}.xlsx`)}
                    />
                    <div className="ask-reply-actions">
                      <button type="button" className="ask-ghost-btn" onClick={() => void copyAnswer(m.text)}>Copy</button>
                      <button type="button" className="ask-ghost-btn" onClick={() => downloadAnswerDoc(questionBefore(idx), m.text)}>
                        Save as Word
                      </button>
                      {m.id && conversationId && (
                        <button type="button" className="ask-ghost-btn" onClick={() => setQueryFor(m)}>Log a query</button>
                      )}
                    </div>
                  </div>
                ),
              )}

              {busy && (
                <div className="ask-reply">
                  {liveText && <AskAnswer text={liveText} />}
                  <div className="ask-status" role="status">
                    <img src={avatar} alt="" className="ask-avatar ask-avatar--sm ask-avatar--pulse" />
                    {step ?? (liveText ? 'Writing' : 'Thinking')} · {elapsed}s
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="ask-dock">
            {error && <div className="ask-error" role="alert">{error}</div>}
            {composer}
            <div className="ask-footnote">
              <span>
                {lastCost !== null && !busy
                  ? `That answer cost about US$${formatNumber(lastCost, 'en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}.`
                  : `${name} reads the portal’s records. Log a query if something looks wrong.`}
              </span>
              {messages.length > 0 && (
                <button type="button" className="ask-ghost-btn ask-save-chat" disabled={busy} onClick={() => downloadChatDoc(chatTitle, messages)}>
                  Save chat as Word
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {queryFor && conversationId && (
        <LogQueryModal
          conversationId={conversationId}
          messageId={queryFor.id || null}
          onClose={() => setQueryFor(null)}
        />
      )}
    </div>
  );
}

function LogQueryModal({ conversationId, messageId, onClose }: { conversationId: string; messageId: string | null; onClose: () => void }) {
  const { supabase } = useAuth();
  const { showToast } = useToast();
  const [kind, setKind] = useState<'wrong' | 'question'>('wrong');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await logQuery(supabase, { conversationId, messageId, kind, comment, files });
      showToast('Query logged. The team will look at it.', 'success');
      onClose();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not log the query', 'error');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Log a query"
      size="md"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div className="form-group">
        <span className="form-label">What is it?</span>
        <div className="btn-grid">
          <button type="button" className={`btn ${kind === 'wrong' ? 'btn-primary' : 'btn-outline'}`} aria-pressed={kind === 'wrong'} onClick={() => setKind('wrong')}>
            Something&apos;s wrong
          </button>
          <button type="button" className={`btn ${kind === 'question' ? 'btn-primary' : 'btn-outline'}`} aria-pressed={kind === 'question'} onClick={() => setKind('question')}>
            I have a question
          </button>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="ask-query-comment">Tell us more</label>
        <textarea
          id="ask-query-comment"
          className="form-textarea"
          rows={4}
          maxLength={4000}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What looks wrong, or what you need to know"
        />
        <span className="form-help">The chat goes with it, so the team can see what was asked and answered.</span>
      </div>
      <AskAttachments onChange={setFiles} disabled={saving} />
    </Modal>
  );
}
